// =============================================================================
// features/sales/agenda/AppointmentCard.tsx — Card canônico de um compromisso
// da agenda comercial (doc 26 §7 — componente nomeado `AppointmentCard`).
//
// Usado em desktop E mobile (mesmo componente — a agenda nunca vira tabela
// comprimida, doc 26 §6.1). Hover: Spotlight + Lift (DS §9.3, Card).
// Ação primária ("Abrir no CRM") fixa no rodapé, alvo ≥44px (WCAG 2.5.5).
//
// Urgência nunca só por cor (doc 26 §7) — badge sempre com texto
// ("Atrasado"/"Em breve"), não só a cor de fundo.
// =============================================================================

import type { Appointment } from '@elemento/shared-schemas';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { Avatar } from '../../../components/ui/Avatar';
import { Badge } from '../../../components/ui/Badge';
import type { AppointmentLeadSummary } from '../../../hooks/sales/useAppointmentLeadSummaries';
import { cn } from '../../../lib/cn';

import {
  CHANNEL_LABELS,
  URGENCY_BADGE_VARIANT,
  URGENCY_LABEL,
  nextActionLabel,
  urgencyLevel,
} from './labels';
import { formatDateTimeOrgTz } from './timezone';

export interface AppointmentCardProps {
  appointment: Appointment;
  now: Date;
  leadSummary: AppointmentLeadSummary | undefined;
  leadResolutionFailed: boolean;
  agentName?: string | undefined;
}

export function AppointmentCard({
  appointment,
  now,
  leadSummary,
  leadResolutionFailed,
  agentName,
}: AppointmentCardProps): React.JSX.Element {
  const cardRef = React.useRef<HTMLDivElement>(null);
  const urgency = urgencyLevel(appointment, now);
  const clientLabel =
    leadSummary?.name ?? (leadResolutionFailed ? 'Lead indisponível' : 'Carregando…');

  const handleMouseMove = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - rect.left}px`);
    el.style.setProperty('--my', `${e.clientY - rect.top}px`);
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    cardRef.current?.style.setProperty('--mx', '-9999px');
    cardRef.current?.style.setProperty('--my', '-9999px');
  }, []);

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={cn(
        'relative overflow-hidden',
        'rounded-md border border-border bg-surface-1 p-4',
        'flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4',
        'transition-[transform,box-shadow,border-color] duration-[250ms] ease-out',
        'hover:-translate-y-[3px] hover:border-border-strong hover:shadow-e4',
        '[--mx:-9999px] [--my:-9999px]',
      )}
      style={{ boxShadow: 'var(--elev-2)' }}
    >
      {/* Spotlight (DS §9.3) */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-md"
        style={{
          background:
            'radial-gradient(360px circle at var(--mx) var(--my), rgba(46,155,62,0.06), transparent 60%)',
        }}
      />

      {/* Cliente */}
      <div className="relative z-10 flex items-center gap-3 min-w-0 sm:w-52 shrink-0">
        <Avatar name={leadSummary?.name ?? '?'} size="md" />
        <div className="min-w-0">
          <p className="font-sans font-semibold text-sm text-ink truncate">{clientLabel}</p>
          {agentName !== undefined && (
            <p className="font-sans text-xs text-ink-3 truncate">Resp.: {agentName}</p>
          )}
        </div>
      </div>

      {/* Motivo + badges */}
      <div className="relative z-10 flex-1 min-w-0 flex flex-col gap-1.5">
        <p className="font-sans text-sm text-ink-2 truncate">
          {appointment.reason ?? 'Sem motivo registrado'}
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="neutral">{CHANNEL_LABELS[appointment.channel]}</Badge>
          {appointment.campaignMemberId !== null && <Badge variant="info">Campanha</Badge>}
          {appointment.status === 'scheduled' && (
            <Badge variant={URGENCY_BADGE_VARIANT[urgency]}>{URGENCY_LABEL[urgency]}</Badge>
          )}
        </div>
      </div>

      {/* Horário + próxima ação */}
      <div className="relative z-10 flex flex-col gap-1 sm:w-56 shrink-0 sm:text-right">
        <p className="font-mono text-sm font-medium text-ink" style={{ letterSpacing: '-0.01em' }}>
          {formatDateTimeOrgTz(appointment.scheduledAt)}
        </p>
        <p className="font-sans text-xs text-ink-3">{nextActionLabel(appointment, now)}</p>
      </div>

      {/* Ação primária — fixa no rodapé do card no mobile (alvo ≥44px) */}
      <Link
        to={`/crm/${appointment.leadId}`}
        className={cn(
          'relative z-10 inline-flex items-center justify-center gap-1.5 shrink-0',
          'min-h-[44px] px-4 rounded-sm',
          'font-sans text-sm font-semibold text-azul',
          'border border-border-strong bg-surface-1',
          'transition-[border-color,color,background] duration-fast ease',
          'hover:border-azul hover:bg-info-bg',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/40',
          'active:bg-surface-muted',
        )}
      >
        Abrir no CRM
      </Link>
    </div>
  );
}
